import React from 'react';

import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { type ApexOptions } from 'apexcharts';
import ApexCharts from 'react-apexcharts';

interface PieChartProps {
  labels: Array<string>;
  series: Array<number>;
  otherApexOptions?: Partial<ApexOptions>;
  chartProps?: Partial<React.ComponentProps<typeof ApexCharts>>;
  percent: Array<number>;
}

export const PieChart: React.FC<PieChartProps> = ({
  labels,
  series,
  otherApexOptions,
  chartProps,
  percent,
}) => {
  const defaultOptions: ApexOptions = {
    chart: {
      type: 'pie',
      height: 350,
    },
    labels,
    legend: {
      show: false,
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 300,
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
    dataLabels: {
      enabled: true,
      formatter: (_, opts: ExplicitAny): string => {
        const actualValue = series[opts.seriesIndex];
        const percentValue = percent[opts.seriesIndex];
        return `${actualValue} (${percentValue?.toFixed(1)}%)`;
      },
    },
    colors: [
      '#3A6DDE',
      '#FB933E',
      '#B56BFF',
      '#1FCD5F',
      '#e91e63',
      '#00bcd4',
      '#4caf50',
      '#ffeb3b',
      '#3f51b5',
      '#9c27b0',
    ],
  };

  const mergedOptions: ApexOptions = { ...defaultOptions, ...otherApexOptions };

  return (
    <Flex alignItems="center" justifyContent="center">
      <Stack p={3}>
        <ApexCharts
          options={mergedOptions}
          series={series}
          type="pie"
          height={300}
          width={320}
          {...chartProps}
        />
      </Stack>
      <Stack ml={5} w="full">
        {labels.map((label, index) => (
          <HStack key={label} mb={2} w="full">
            <Box
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: defaultOptions.colors![index],
              }}
            />
            <Flex justifyContent="space-between" w="full">
              <Text
                color="gray.600"
                fontSize="13px"
                maxWidth={250}
                mb={1}
                whiteSpace="nowrap"
                mr={5}
              >
                {label}
              </Text>
              <Text fontSize="13px" color="gray.900">
                {series[index]} ({percent[index]?.toFixed(1)}%)
              </Text>
            </Flex>
          </HStack>
        ))}
      </Stack>
    </Flex>
  );
};
